import React from "react";
import styled from "@emotion/styled";
import {COLORS} from "../../../constants/constants";
import {jobStopTypeToString} from "../../../utils/stops/StopUtils";
import {CSV_IMPORT_COLUMNS} from "./CsvImport.service";
import {CsvImportOrder, CsvImportStop} from "./types";

type CsvImportStopsTableProps = {
  orders: CsvImportOrder[];
  // Header for the OrderNumber column (the file's grouping key)
  orderNumberLabel: string;
  testId?: string;
};

const formatDateTime = (value?: string) => {
  if (!value) return "";
  const dateTime = value.replace("T", " ");
  return dateTime.endsWith(":00") ? dateTime.slice(0, 16) : dateTime;
};

const cellValue = (key: string, order: CsvImportOrder, stop: CsvImportStop) => {
  switch (key) {
    case "orderNumber":
      return order.orderNumber;
    case "customerCode":
      return order.customerCode;
    case "service":
      return order.service;
    case "orderReference":
      return order.orderReference;
    case "pieces":
      return stop.pieces?.toString();
    case "weight":
      return stop.weight?.toString();
    case "vehicle":
      return order.vehicle;
    case "caller":
      return order.caller;
    case "stopType":
      return jobStopTypeToString({value: stop.stopType});
    case "earlyDateTime":
    case "scheduledDateTime":
    case "lateDateTime":
      return formatDateTime(stop[key]);
    default:
      return stop[key as keyof CsvImportStop]?.toString();
  }
};

/** The file's rows: one per stop with every schema column, in schema order; rows of the same order share a shade. */
// Memoized: the preview can list thousands of stops, and the dialog re-renders on every status poll and checkbox click
const CsvImportStopsTable = React.memo(({orders, orderNumberLabel, testId}: CsvImportStopsTableProps) => (
  <TableFrame data-testid={testId}>
    <Table>
      <thead>
        <tr>
          {CSV_IMPORT_COLUMNS.map((column) => (
            <th key={column.key}>{column.key === "orderNumber" ? orderNumberLabel : column.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {orders.map((order, orderIndex) =>
          order.stops.map((stop) => (
            <StopRow key={`${order.orderNumber}-${stop.rowNumber}`} isShaded={orderIndex % 2 === 1}>
              {CSV_IMPORT_COLUMNS.map((column) => (
                <td key={column.key}>{cellValue(column.key, order, stop) || <Empty>—</Empty>}</td>
              ))}
            </StopRow>
          ))
        )}
      </tbody>
    </Table>
  </TableFrame>
));
CsvImportStopsTable.displayName = "CsvImportStopsTable";

export const TableFrame = styled.div`
  max-height: 320px;
  overflow: auto;
  border: 1px solid ${COLORS.COLOR_BORDER_LIGHT};
  border-radius: 10px;
`;

export const Table = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;

  th,
  td {
    padding: 8px 12px;
    text-align: left;
    white-space: nowrap;
    border-bottom: 1px solid ${COLORS.COLOR_BORDER_LIGHT};
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${COLORS.BG_GRAY_LIGHT};
    color: ${COLORS.TEXT_GRAY_MEDIUM};
    font-weight: 600;
  }

  tbody tr:last-of-type td {
    border-bottom: none;
  }
`;

export const StopRow = styled.tr<{isShaded: boolean}>`
  background: ${(props) => (props.isShaded ? COLORS.BG_GRAY_LIGHTER : COLORS.WHITE)};
`;

export const Empty = styled.span`
  color: ${COLORS.COLOR_PLACE_HOLDER};
`;

export default CsvImportStopsTable;
